import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { NavArrow } from './NavArrow';
import { PrintView } from './PrintView';
import { SlideContextProvider } from './SlideContext';
import { SlideFrame } from './SlideFrame';
import { SIDEBAR_WIDTH, SlideIndex } from './SlideIndex';
import { Stoplight } from './Stoplight';
import { DeckWordmark } from './DeckWordmark';
import { StoryPage } from './StoryPage';
import { useIsMobile } from './useIsMobile';
import { useDeckViewTracking } from './useDeckViewTracking';
import { resolveDeck } from './registry';
import type { Deck, PrintPart } from './types';

function parseHash(total: number): number {
  const raw = window.location.hash.replace(/^#/, '').trim();
  // Accept both "#N" and "#slide-N" so anchor links from SupLink work.
  const m = raw.match(/^(?:slide-)?(\d+)$/);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(total - 1, n - 1));
}

function DeckRunner({ deck }: { deck: Deck }) {
  const [index, setIndex] = useState(() => parseHash(deck.slides.length));
  useDeckViewTracking(deck, index, 'desktop');

  useEffect(() => {
    const onHash = () => setIndex(parseHash(deck.slides.length));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [deck.slides.length]);

  useEffect(() => {
    const desired = `#${index + 1}`;
    if (window.location.hash !== desired) {
      window.history.replaceState(null, '', `${window.location.pathname}${desired}`);
    }
  }, [index]);

  useEffect(() => {
    const slide = deck.slides[index];
    if (slide) {
      document.title = `${deck.title} — ${slide.title}`;
    }
  }, [deck, index]);

  const move = useCallback(
    (delta: number) => {
      setIndex((prev) => {
        const next = prev + delta;
        if (next < 0) return 0;
        if (next >= deck.slides.length) return deck.slides.length - 1;
        return next;
      });
    },
    [deck.slides.length]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          move(1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          move(-1);
          break;
        case 'Home':
          e.preventDefault();
          setIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setIndex(deck.slides.length - 1);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move, deck.slides.length]);

  const current = deck.slides[index];
  if (!current) return null;
  const Slide = current.Component;
  const atFirst = index === 0;
  const atLast = index === deck.slides.length - 1;

  return (
    <div className="fixed inset-0 overflow-hidden bg-blue-950">
      <SlideIndex slides={deck.slides} currentIndex={index} onSelect={setIndex} />
      <a
        href={`${window.location.pathname}?print=1${window.location.hash || ''}`}
        target="_blank"
        rel="noreferrer"
        className="fixed right-4 top-4 z-20 hidden rounded-md bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-md ring-1 ring-slate-300 transition hover:bg-white hover:text-blue-700 md:inline-flex"
        aria-label="Open print view"
      >
        Print view
      </a>
      <SlideFrame
        leftGutter={SIDEBAR_WIDTH}
        cornerSlot={
          index > 0 || current.stoplight ? (
            <div className="flex items-center gap-4">
              {index > 0 ? <DeckWordmark /> : null}
              {current.stoplight ? <Stoplight state={current.stoplight} /> : null}
            </div>
          ) : null
        }
      >
        <SlideContextProvider index={index} total={deck.slides.length} slides={deck.slides}>
          <Slide title={current.title} index={index} total={deck.slides.length} />
        </SlideContextProvider>
      </SlideFrame>
      <NavArrow direction="up" disabled={atFirst} onClick={() => move(-1)} label="Previous slide" />
      <NavArrow direction="down" disabled={atLast} onClick={() => move(1)} label="Next slide" />
    </div>
  );
}

function printPart(search: string): PrintPart | undefined {
  const part = new URLSearchParams(search).get('part');
  return part === 'main' || part === 'appendix' ? part : undefined;
}

function DeckNotFound() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-blue-950 text-slate-200">
      <div className="text-center">
        <p className="mb-2 text-sm uppercase tracking-[0.2em] text-slate-400">404</p>
        <p className="text-lg">Deck not found.</p>
      </div>
    </div>
  );
}

export function DeckPage() {
  const { round, deckSlug, version } = useParams<{
    round: string;
    deckSlug: string;
    version: string;
  }>();
  const isMobile = useIsMobile();
  const location = useLocation();
  const isPrint = useMemo(
    () => new URLSearchParams(location.search).get('print') === '1',
    [location.search]
  );
  const deck = resolveDeck(round, deckSlug, version);
  if (!deck) return <DeckNotFound />;
  if (isPrint) return <PrintView deck={deck} part={printPart(location.search)} />;
  return isMobile ? <StoryPage deck={deck} /> : <DeckRunner deck={deck} />;
}
